// ============================================
// INVESTOR SURVEY — Interactive in-app survey
// Large text, high contrast, big touch targets
// ============================================

import React, { useState, useCallback, useMemo, useRef } from 'react';
import { useLanguage } from './LanguageContext';
import {
  Send, Check, ChevronUp, ChevronDown,
  MessageSquare, Award, X, Mic, Square, Loader
} from 'lucide-react';

interface SurveyViewProps {
  currentInvestor: any;
  apiUrl: string;
}

interface SurveyAnswers {
  q1: string;
  q2: string[];
  q3: number | null;
  q4: string[];
  q5: string;
  q6: string[];
  q7: string;
  q7Other: string;
  q8: string;
  q8Other: string;
  q9: string;
}

const INITIAL_ANSWERS: SurveyAnswers = {
  q1: '', q2: [], q3: null, q4: [], q5: '',
  q6: [], q7: '', q7Other: '', q8: '', q8Other: '', q9: '',
};

const GOLD = '#D6B160';
const DARK = '#1a1a2e';

/**
 * Answer options are STORED and POSTED to /api/survey, so the option strings below
 * stay English forever — analysing responses must not depend on the investor's UI
 * language. Each map only resolves a value to its DISPLAY translation key.
 */
const Q1_LABELS: Record<string, string> = {
  'More than 5': 'svQ1Opt4',
};
const Q2_LABELS: Record<string, string> = {
  'Viewing my property portfolio': 'svQ2Opt1',
  'Uploading documents': 'svQ2Opt2',
  'AI assistant': 'svQ2Opt3',
  'Payment schedule tracking': 'svQ2Opt4',
  'Market reports': 'svQ2Opt5',
};
const Q4_LABELS: Record<string, string> = {
  'Uploading documents': 'svQ4Opt1',
  'Understanding my portfolio at a glance': 'svQ4Opt2',
  'Keeping track of insurance and ownership paperwork': 'svQ4Opt3',
  'Reviewing potential new properties': 'svQ4Opt4',
  'Knowing what to do next as an owner': 'svQ4Opt5',
};
const Q6_LABELS: Record<string, string> = {
  'Better overview of all my properties': 'svQ6Opt1',
  'Simpler document organization': 'svQ6Opt2',
  'Reminders for important deadlines or renewals': 'svQ6Opt3',
  'Better insights before buying another property': 'svQ6Opt4',
  'Resale support': 'svQ6Opt5',
};
const Q7_LABELS: Record<string, string> = {
  'Better portfolio dashboard': 'svQ7Opt1',
  'Document reminders and alerts': 'svQ7Opt2',
  'Resale support': 'svQ7Opt3',
  'Property comparison tools': 'svQ7Opt4',
  'Advisor support / chat': 'svQ7Opt5',
  Other: 'svOther',
};
const Q8_LABELS: Record<string, string> = {
  'Portfolio overview': 'svQ8Opt1',
  'Documents and reminders': 'svQ8Opt2',
  'Buying support': 'svQ8Opt3',
  'Selling support': 'svQ8Opt4',
  'Rental support': 'svQ8Opt5',
  Other: 'svOther',
};

// ---- RANKING ----
function RankingQuestion({ items, value, onChange, labelFor }: {
  items: string[]; value: string[]; onChange: (v: string[]) => void;
  /** Display-only translator; the ranked values handed back stay the English items. */
  labelFor?: (item: string) => string;
}) {
  const { t } = useLanguage();
  const ranked = value.length > 0 ? value : [];
  const unranked = items.filter(i => !ranked.includes(i));
  const label = (item: string) => (labelFor ? labelFor(item) : item);

  const moveUp = (idx: number) => {
    if (idx === 0) return;
    const next = [...ranked];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    onChange(next);
  };
  const moveDown = (idx: number) => {
    if (idx === ranked.length - 1) return;
    const next = [...ranked];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    onChange(next);
  };

  return (
    <div>
      {ranked.map((item, idx) => (
        <div key={item} style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '14px 16px', marginBottom: 8,
          background: '#FFF8E7', borderRadius: 12,
          border: `2px solid ${GOLD}`,
        }}>
          <div style={{
            width: 32, height: 32, lineHeight: '32px', textAlign: 'center',
            background: GOLD, color: DARK, borderRadius: '50%',
            fontWeight: 800, fontSize: 15, flexShrink: 0,
          }}>{idx + 1}</div>
          <span style={{ flex: 1, fontSize: 16, color: '#1a1a1a', fontWeight: 500 }}>{label(item)}</span>
          <button type="button" onClick={() => moveUp(idx)} disabled={idx === 0}
            style={{ background: idx === 0 ? '#f0f0f0' : '#fff', border: '1px solid #ddd', borderRadius: 6, padding: '4px 6px', cursor: idx === 0 ? 'default' : 'pointer', opacity: idx === 0 ? 0.3 : 1 }}>
            <ChevronUp size={18} color="#374151" />
          </button>
          <button type="button" onClick={() => moveDown(idx)} disabled={idx === ranked.length - 1}
            style={{ background: idx === ranked.length - 1 ? '#f0f0f0' : '#fff', border: '1px solid #ddd', borderRadius: 6, padding: '4px 6px', cursor: idx === ranked.length - 1 ? 'default' : 'pointer', opacity: idx === ranked.length - 1 ? 0.3 : 1 }}>
            <ChevronDown size={18} color="#374151" />
          </button>
          <button type="button" onClick={() => onChange(ranked.filter(r => r !== item))}
            style={{ background: '#fff', border: '1px solid #ddd', borderRadius: 6, padding: '4px 6px', cursor: 'pointer' }}>
            <X size={18} color="#999" />
          </button>
        </div>
      ))}
      {unranked.length > 0 && ranked.length === 0 && (
        <p style={{ fontSize: 14, color: '#999', margin: '0 0 8px' }}>{t('svRankHint')}</p>
      )}
      {unranked.map((item) => (
        <button key={item} type="button" onClick={() => onChange([...ranked, item])}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 12, width: '100%',
            padding: '14px 16px', marginBottom: 8,
            background: '#fff', borderRadius: 12,
            border: '2px solid #e8e8e8', cursor: 'pointer',
            textAlign: 'left', fontSize: 16, color: '#555',
            transition: 'border-color 0.15s',
          }}>
          <div style={{
            width: 32, height: 32, lineHeight: '32px', textAlign: 'center',
            background: '#f5f5f5', color: '#bbb', borderRadius: '50%',
            fontWeight: 700, fontSize: 14, flexShrink: 0,
          }}>+</div>
          <span style={{ textAlign: 'left' }}>{label(item)}</span>
        </button>
      ))}
    </div>
  );
}

const SurveyView: React.FC<SurveyViewProps> = ({ currentInvestor, apiUrl }) => {
  const { t } = useLanguage();
  const [answers, setAnswers] = useState<SurveyAnswers>(INITIAL_ANSWERS);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Voice recording state
  const [recordingFor, setRecordingFor] = useState<string | null>(null); // 'q5' or 'q9'
  const [recordingTime, setRecordingTime] = useState(0);
  const [transcribing, setTranscribing] = useState<string | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const recordingMimeTypeRef = useRef<string>('audio/webm');

  const getSupportedMimeType = () => {
    const types = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus', 'audio/wav'];
    for (const type of types) {
      if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(type)) return type;
    }
    return null;
  };

  const startRecording = async (qKey: string) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = getSupportedMimeType();
      recordingMimeTypeRef.current = mimeType || 'audio/webm';
      const opts: MediaRecorderOptions = mimeType ? { mimeType } : {};
      const recorder = new MediaRecorder(stream, opts);
      audioChunksRef.current = [];

      recorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        if (recordingTimerRef.current) { clearInterval(recordingTimerRef.current); recordingTimerRef.current = null; }
        setRecordingTime(0);
        setRecordingFor(null);

        const blob = new Blob(audioChunksRef.current, { type: recordingMimeTypeRef.current });
        if (blob.size === 0) return;

        // Convert to base64 and transcribe
        setTranscribing(qKey);
        try {
          const reader = new FileReader();
          const base64 = await new Promise<string>((resolve) => {
            reader.onloadend = () => {
              const result = reader.result as string;
              resolve(result.split(',')[1]);
            };
            reader.readAsDataURL(blob);
          });

          const token = localStorage.getItem('authToken');
          const res = await fetch(`${apiUrl}/voice/transcribe`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({
              audioBase64: base64,
              mimeType: recordingMimeTypeRef.current,
              investorId: currentInvestor?.id,
              investorName: currentInvestor?.name,
              investorEmail: currentInvestor?.email,
            }),
          });
          const data = await res.json();
          if (data.transcription || data.reply || data.transcript || data.text) {
            const text = data.transcription || data.transcript || data.text || data.reply || '';
            setAnswers(prev => ({
              ...prev,
              [qKey]: prev[qKey as keyof SurveyAnswers] ? prev[qKey as keyof SurveyAnswers] + ' ' + text : text,
            }));
          }
        } catch (err) {
          console.error('Transcription failed:', err);
        } finally {
          setTranscribing(null);
        }
      };

      mediaRecorderRef.current = recorder;
      recorder.start(250);
      setRecordingFor(qKey);
      setRecordingTime(0);

      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev + 1 >= 60) stopRecording();
          return prev + 1;
        });
      }, 1000);
    } catch (err: any) {
      if (err?.name === 'NotAllowedError') {
        alert(t('svMicDenied'));
      } else {
        alert(t('svMicUnsupported'));
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
  };

  const updateAnswer = useCallback((key: keyof SurveyAnswers, value: any) => {
    setAnswers(prev => ({ ...prev, [key]: value }));
  }, []);

  const toggleMulti = useCallback((key: 'q2', value: string) => {
    setAnswers(prev => {
      const arr = prev[key] as string[];
      return { ...prev, [key]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value] };
    });
  }, []);

  const handleSubmit = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${apiUrl}/survey`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          investorId: currentInvestor?.id,
          investorName: currentInvestor?.name || 'Anonymous',
          investorEmail: currentInvestor?.email || '',
          answers,
          submittedAt: new Date().toISOString(),
        }),
      });
      // Translation keys, not literals — resolved through t() where the banner renders.
      if (!response.ok) throw new Error('svSubmitFailed');
      setSubmitted(true);
    } catch (err: any) {
      setError(err.message || 'svGenericError');
    } finally {
      setSubmitting(false);
    }
  }, [answers, apiUrl, currentInvestor]);

  const progress = useMemo(() => {
    let n = 0;
    if (answers.q1) n++;
    if (answers.q2.length > 0) n++;
    if (answers.q3 !== null) n++;
    if (answers.q4.length > 0) n++;
    if (answers.q5.trim()) n++;
    if (answers.q6.length > 0) n++;
    if (answers.q7) n++;
    if (answers.q8) n++;
    if (answers.q9.trim()) n++;
    return n;
  }, [answers]);

  const isValid = answers.q1 && answers.q3 !== null;

  // ---- SUCCESS ----
  if (submitted) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '70vh', padding: 32 }}>
        <div style={{
          width: 96, height: 96, borderRadius: '50%',
          background: 'linear-gradient(135deg, #22c55e, #16a34a)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: 28, boxShadow: '0 12px 32px rgba(34,197,94,0.25)',
        }}>
          <Check size={48} color="#fff" strokeWidth={3} />
        </div>
        <h2 style={{ fontSize: 28, fontWeight: 800, color: '#1a1a1a', margin: '0 0 12px' }}>{t('svThankYou')}</h2>
        <p style={{ fontSize: 18, color: '#666', maxWidth: 420, lineHeight: 1.7, textAlign: 'center', margin: '0 0 28px' }}>
          {t('svThankYouDesc')}
        </p>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 10,
          background: '#FFF8E7', borderRadius: 12, padding: '14px 24px',
          border: `2px solid ${GOLD}`,
        }}>
          <Award size={22} color={GOLD} />
          <span style={{ fontSize: 16, color: DARK, fontWeight: 700 }}>{t('svRecorded')}</span>
        </div>
      </div>
    );
  }

  /** Display label for a stored answer value; falls back to the value when unmapped. */
  const optLabel = (map: Record<string, string>, value: string) => (map[value] ? t(map[value]) : value);

  // ---- QUESTION CARD ----
  const Q = ({ num, title, format, children }: { num: number; title: string; format: string; children: React.ReactNode }) => (
    <div style={{
      background: '#fff', borderRadius: 16, padding: 24,
      marginBottom: 16, border: '1px solid #eee',
      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 18 }}>
        <div style={{
          width: 40, height: 40, lineHeight: '40px', textAlign: 'center',
          background: `linear-gradient(135deg, ${DARK}, #16213e)`,
          color: GOLD, borderRadius: 10, fontSize: 16, fontWeight: 800, flexShrink: 0,
        }}>{num}</div>
        <div style={{ paddingTop: 2 }}>
          <p style={{ fontSize: 18, fontWeight: 700, color: '#1a1a1a', margin: 0, lineHeight: 1.4 }}>{title}</p>
          <p style={{ fontSize: 12, color: GOLD, fontWeight: 700, margin: '4px 0 0', textTransform: 'uppercase', letterSpacing: 1 }}>{format}</p>
        </div>
      </div>
      {children}
    </div>
  );

  // ---- RADIO ----
  const Radio = ({ label, selected, onSelect }: { label: string; selected: boolean; onSelect: () => void }) => (
    <button type="button" onClick={onSelect}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 14, width: '100%',
        padding: '14px 18px', marginBottom: 8,
        background: selected ? '#FFF8E7' : '#fafafa',
        borderRadius: 12,
        border: selected ? `2.5px solid ${GOLD}` : '2px solid #eee',
        cursor: 'pointer', textAlign: 'left', fontSize: 17,
        color: selected ? '#1a1a1a' : '#444',
        fontWeight: selected ? 600 : 400,
        transition: 'all 0.15s',
      }}>
      <div style={{
        width: 24, height: 24, borderRadius: '50%',
        border: selected ? `7px solid ${GOLD}` : '2.5px solid #bbb',
        background: '#fff', flexShrink: 0, transition: 'all 0.15s',
      }} />
      <span style={{ textAlign: 'left' }}>{label}</span>
    </button>
  );

  // ---- CHECKBOX ----
  const Chk = ({ label, checked, onToggle }: { label: string; checked: boolean; onToggle: () => void }) => (
    <button type="button" onClick={onToggle}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 14, width: '100%',
        padding: '14px 18px', marginBottom: 8,
        background: checked ? '#FFF8E7' : '#fafafa',
        borderRadius: 12,
        border: checked ? `2.5px solid ${GOLD}` : '2px solid #eee',
        cursor: 'pointer', textAlign: 'left', fontSize: 17,
        color: checked ? '#1a1a1a' : '#444',
        fontWeight: checked ? 600 : 400,
        transition: 'all 0.15s',
      }}>
      <div style={{
        width: 24, height: 24, borderRadius: 6,
        border: checked ? 'none' : '2.5px solid #bbb',
        background: checked ? GOLD : '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, transition: 'all 0.15s',
      }}>
        {checked && <Check size={16} color={DARK} strokeWidth={3} />}
      </div>
      <span style={{ textAlign: 'left' }}>{label}</span>
    </button>
  );

  // ---- OTHER OPTION ----
  const OtherRadio = ({ qKey, otherKey }: { qKey: 'q7' | 'q8'; otherKey: 'q7Other' | 'q8Other' }) => (
    <>
      <Radio label={t('svOther')} selected={answers[qKey] === 'Other'} onSelect={() => updateAnswer(qKey, 'Other')} />
      {answers[qKey] === 'Other' && (
        <input type="text" value={answers[otherKey]} onChange={(e) => updateAnswer(otherKey, e.target.value)}
          placeholder={t('svSpecify')}
          style={{
            width: '100%', padding: '14px 18px', borderRadius: 12,
            border: `2px solid ${GOLD}`, fontSize: 17, marginBottom: 8,
            fontFamily: 'inherit', color: '#333', boxSizing: 'border-box', outline: 'none',
          }}
        />
      )}
    </>
  );

  // ---- SECTION DIVIDER ----
  const Section = ({ label }: { label: string }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, margin: '32px 0 20px' }}>
      <span style={{ fontSize: 13, fontWeight: 800, color: GOLD, textTransform: 'uppercase', letterSpacing: 2, whiteSpace: 'nowrap' }}>{label}</span>
      <div style={{ flex: 1, height: 2, background: `linear-gradient(to right, ${GOLD}40, transparent)` }} />
    </div>
  );

  return (
    <div style={{ padding: '12px 16px 140px', maxWidth: 720, margin: '0 auto' }}>
      <style>{`
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.7; } }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>

      {/* HEADER */}
      <div style={{
        padding: '32px 28px 28px', marginBottom: 8,
        background: `linear-gradient(135deg, ${DARK}, #16213e)`,
        borderRadius: 20, position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: -40, right: -40, width: 120, height: 120, borderRadius: '50%', background: 'rgba(214,177,96,0.07)' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 8 }}>
          <MessageSquare size={28} color={GOLD} />
          <h1 style={{ fontSize: 24, fontWeight: 800, color: '#fff', margin: 0 }}>{t('svHeaderTitle')}</h1>
        </div>
        <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.5)', margin: '0 0 22px' }}>
          {t('svHeaderSub')}
        </p>
        <div style={{ maxWidth: 360 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', fontWeight: 600 }}>{t('svProgress')}</span>
            <span style={{ fontSize: 12, color: GOLD, fontWeight: 700 }}>{progress} {t('svOf')} 9</span>
          </div>
          <div style={{ height: 6, background: 'rgba(255,255,255,0.1)', borderRadius: 6, overflow: 'hidden' }}>
            <div style={{
              height: '100%', width: `${(progress / 9) * 100}%`,
              background: `linear-gradient(90deg, ${GOLD}, #e8c97a)`,
              borderRadius: 6, transition: 'width 0.4s ease',
            }} />
          </div>
        </div>
      </div>

      {/* SECTION 1 */}
      <Section label={t('svSection1')} />

      <Q num={1} title={t('svQ1')} format={t('svFmtOne')}>
        {['1', '2', '3–5', 'More than 5'].map(opt => (
          <Radio key={opt} label={optLabel(Q1_LABELS, opt)} selected={answers.q1 === opt} onSelect={() => updateAnswer('q1', opt)} />
        ))}
      </Q>

      <Q num={2} title={t('svQ2')} format={t('svFmtMulti')}>
        {['Viewing my property portfolio', 'Uploading documents', 'AI assistant', 'Payment schedule tracking', 'Market reports'].map(opt => (
          <Chk key={opt} label={optLabel(Q2_LABELS, opt)} checked={answers.q2.includes(opt)} onToggle={() => toggleMulti('q2', opt)} />
        ))}
      </Q>

      <Q num={3} title={t('svQ3')} format={t('svFmtRate')}>
        <div style={{ display: 'flex', gap: 12, padding: '4px 0 8px', flexWrap: 'wrap' }}>
          {[1, 2, 3, 4, 5].map(n => (
            <button key={n} type="button" onClick={() => updateAnswer('q3', n)}
              style={{
                width: 56, height: 56, borderRadius: '50%',
                border: answers.q3 === n ? `3px solid ${GOLD}` : '2.5px solid #ddd',
                background: answers.q3 === n ? GOLD : '#fff',
                color: answers.q3 === n ? DARK : '#555',
                fontSize: 20, fontWeight: 800,
                cursor: 'pointer', transition: 'all 0.2s',
                boxShadow: answers.q3 === n ? '0 4px 16px rgba(214,177,96,0.4)' : 'none',
                transform: answers.q3 === n ? 'scale(1.1)' : 'scale(1)',
              }}>
              {n}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', maxWidth: 310, padding: '0 4px' }}>
          <span style={{ fontSize: 13, color: '#aaa' }}>{t('svRateLow')}</span>
          <span style={{ fontSize: 13, color: '#aaa' }}>{t('svRateHigh')}</span>
        </div>
      </Q>

      {/* SECTION 2 */}
      <Section label={t('svSection2')} />

      <Q num={4} title={t('svQ4')} format={t('svFmtRank')}>
        <RankingQuestion
          items={['Uploading documents', 'Understanding my portfolio at a glance', 'Keeping track of insurance and ownership paperwork', 'Reviewing potential new properties', 'Knowing what to do next as an owner']}
          value={answers.q4} onChange={(v) => updateAnswer('q4', v)}
          labelFor={(item) => optLabel(Q4_LABELS, item)}
        />
      </Q>

      <Q num={5} title={t('svQ5')} format={t('svFmtVoice')}>
        <textarea value={answers.q5} onChange={(e) => updateAnswer('q5', e.target.value)}
          placeholder={t('svVoicePlaceholder')}
          rows={4}
          style={{
            width: '100%', padding: '14px 18px', borderRadius: 12,
            border: '2px solid #eee', fontSize: 17, resize: 'vertical',
            fontFamily: 'inherit', color: '#333', lineHeight: 1.6,
            boxSizing: 'border-box', outline: 'none', transition: 'border-color 0.15s',
          }}
          onFocus={(e) => e.target.style.borderColor = GOLD}
          onBlur={(e) => e.target.style.borderColor = '#eee'}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10 }}>
          {recordingFor === 'q5' ? (
            <button type="button" onClick={stopRecording}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-start', gap: 10,
                padding: '12px 20px', borderRadius: 12,
                background: '#dc2626', color: '#fff',
                fontSize: 15, fontWeight: 600, cursor: 'pointer',
                animation: 'pulse 1.5s infinite',
              }}>
              <Square size={18} fill="#fff" /> {t('svStopRecording')} ({recordingTime}s)
            </button>
          ) : transcribing === 'q5' ? (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 10,
              padding: '12px 20px', borderRadius: 12,
              background: '#f5f5f5', color: '#666', fontSize: 15, fontWeight: 500,
            }}>
              <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} /> {t('svTranscribing')}
            </div>
          ) : (
            <button type="button" onClick={() => startRecording('q5')}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-start', gap: 10,
                padding: '12px 20px', borderRadius: 12,
                background: `linear-gradient(135deg, ${DARK}, #16213e)`, color: GOLD,
                fontSize: 15, fontWeight: 600, cursor: 'pointer',
              }}>
              <Mic size={20} /> {t('svRecordVoice')}
            </button>
          )}
        </div>
      </Q>

      {/* SECTION 3 */}
      <Section label={t('svSection3')} />

      <Q num={6} title={t('svQ6')} format={t('svFmtRank')}>
        <RankingQuestion
          items={['Better overview of all my properties', 'Simpler document organization', 'Reminders for important deadlines or renewals', 'Better insights before buying another property', 'Resale support']}
          value={answers.q6} onChange={(v) => updateAnswer('q6', v)}
          labelFor={(item) => optLabel(Q6_LABELS, item)}
        />
      </Q>

      <Q num={7} title={t('svQ7')} format={t('svFmtOne')}>
        {['Better portfolio dashboard', 'Document reminders and alerts', 'Resale support', 'Property comparison tools', 'Advisor support / chat'].map(opt => (
          <Radio key={opt} label={optLabel(Q7_LABELS, opt)} selected={answers.q7 === opt} onSelect={() => updateAnswer('q7', opt)} />
        ))}
        <OtherRadio qKey="q7" otherKey="q7Other" />
      </Q>

      <Q num={8} title={t('svQ8')} format={t('svFmtOne')}>
        {['Portfolio overview', 'Documents and reminders', 'Buying support', 'Selling support', 'Rental support'].map(opt => (
          <Radio key={opt} label={optLabel(Q8_LABELS, opt)} selected={answers.q8 === opt} onSelect={() => updateAnswer('q8', opt)} />
        ))}
        <OtherRadio qKey="q8" otherKey="q8Other" />
      </Q>

      <Q num={9} title={t('svQ9')} format={t('svFmtVoice')}>
        <textarea value={answers.q9} onChange={(e) => updateAnswer('q9', e.target.value)}
          placeholder={t('svVoicePlaceholder')}
          rows={4}
          style={{
            width: '100%', padding: '14px 18px', borderRadius: 12,
            border: '2px solid #eee', fontSize: 17, resize: 'vertical',
            fontFamily: 'inherit', color: '#333', lineHeight: 1.6,
            boxSizing: 'border-box', outline: 'none', transition: 'border-color 0.15s',
          }}
          onFocus={(e) => e.target.style.borderColor = GOLD}
          onBlur={(e) => e.target.style.borderColor = '#eee'}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10 }}>
          {recordingFor === 'q9' ? (
            <button type="button" onClick={stopRecording}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-start', gap: 10,
                padding: '12px 20px', borderRadius: 12,
                background: '#dc2626', color: '#fff',
                fontSize: 15, fontWeight: 600, cursor: 'pointer',
                animation: 'pulse 1.5s infinite',
              }}>
              <Square size={18} fill="#fff" /> {t('svStopRecording')} ({recordingTime}s)
            </button>
          ) : transcribing === 'q9' ? (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 10,
              padding: '12px 20px', borderRadius: 12,
              background: '#f5f5f5', color: '#666', fontSize: 15, fontWeight: 500,
            }}>
              <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} /> {t('svTranscribing')}
            </div>
          ) : (
            <button type="button" onClick={() => startRecording('q9')}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-start', gap: 10,
                padding: '12px 20px', borderRadius: 12,
                background: `linear-gradient(135deg, ${DARK}, #16213e)`, color: GOLD,
                fontSize: 15, fontWeight: 600, cursor: 'pointer',
              }}>
              <Mic size={20} /> {t('svRecordVoice')}
            </button>
          )}
        </div>
      </Q>

      {/* ERROR */}
      {error && (
        <div style={{
          background: '#FEF2F2', border: '2px solid #fca5a5', borderRadius: 12,
          padding: '14px 18px', marginBottom: 16, color: '#b91c1c', fontSize: 16, fontWeight: 500,
        }}>{t(error)}</div>
      )}

      {/* SUBMIT */}
      <div style={{ marginTop: 12 }}>
        <button type="button" onClick={handleSubmit} disabled={!isValid || submitting}
          style={{
            padding: '18px 56px', borderRadius: 14,
            background: isValid ? `linear-gradient(135deg, ${GOLD}, #c4a555)` : '#ddd',
            color: isValid ? DARK : '#999',
            border: 'none', cursor: isValid ? 'pointer' : 'not-allowed',
            fontSize: 18, fontWeight: 800, letterSpacing: 0.5,
            display: 'inline-flex', alignItems: 'center', gap: 12,
            boxShadow: isValid ? '0 6px 24px rgba(214,177,96,0.35)' : 'none',
            transition: 'all 0.2s',
          }}>
          {submitting ? t('svSubmitting') : <><Send size={20} /> {t('svSubmitSurvey')}</>}
        </button>
        <p style={{ fontSize: 14, color: '#aaa', marginTop: 12, lineHeight: 1.5 }}>
          {t('svConfidential')}
        </p>
      </div>
    </div>
  );
};

export default SurveyView;
